import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  X, Sparkles, User, Calendar, Hash, ChevronDown, ArrowRight,
  Send, StickyNote, Loader2, Mail, AlertTriangle, Zap,
  Brain, TrendingDown, TrendingUp, Minus, RefreshCw,
  ShieldAlert, Bot, Wand2,
  Bold, Italic, Underline, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link2, Quote, Undo2, Redo2, Indent, Outdent,
  RemoveFormatting, Type, Code,
  Paperclip, Image, Film, Smile, Languages, Check, Globe,
} from 'lucide-react';

import {
  TicketDetail, TicketMessage, TicketAttachment, Macro,
  registerTicketViewer, unregisterTicketViewer, getTicketViewers, TicketViewer,
  sendChatMessage, uploadTicketAttachment, translateText,
  getUserPreferences, updateUserPreferences,
} from '../../services/api';
import { StatusBadge, TicketStatus } from './StatusBadge';
import { PriorityBadge } from './PriorityBadge';
import { SnoozePicker } from './SnoozePicker';
import { SlaTimer } from './SlaTimer';
import { MacroSelector } from './MacroSelector';

interface AgentOption {
  id: string;
  name: string;
}

interface TicketDetailPanelProps {
  ticketDetail: TicketDetail | null | undefined;
  loading?: boolean;
  agents?: AgentOption[];
  onStatusChange?: (ticketId: string, status: string) => void;
  onSnooze?: (ticketId: string, snoozeUntil: string | null) => void;
  onAddMessage?: (ticketId: string, content: string, type: 'email' | 'note', isDraft?: boolean, attachments?: TicketAttachment[]) => Promise<void> | void;
  onAssign?: (ticketId: string, agentId: string, agentName: string) => void;
  onTagsChange?: (ticketId: string, tags: string[]) => void;
  onClose?: () => void;
}

const STATUS_OPTIONS: Array<{ value: TicketStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const CATEGORY_LABELS: Record<string, string> = {
  ShippingDelivery: 'Shipping & Delivery',
  ReturnsCancellationsExchanges: 'Returns & Exchanges',
  PaymentBilling: 'Payment & Billing',
  ProductInfoAvailability: 'Product Info',
  CustomerComplaintsSatisfaction: 'Customer Complaint',
  AccountProfileOther: 'Account & Profile',
  PromotionsDiscounts: 'Promotions',
  TechnicalSupport: 'Technical Support',
  GeneralInquiry: 'General Inquiry',
};

function textToHtml(text: string) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}

function htmlToPlainText(html: string) {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').trim();
}

function sanitizeHtml(html: string) {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+=("[^"]*"|'[^']*')/gi, '')
    .replace(/javascript:/gi, '');
}

export function TicketDetailPanel({
  ticketDetail,
  loading,
  agents = [],
  onStatusChange,
  onSnooze,
  onAddMessage,
  onAssign,
  onClose,
}: TicketDetailPanelProps) {
  const [replyContent, setReplyContent] = useState('');
  const [replyType, setReplyType] = useState<'email' | 'note'>('email');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(false);
  const [otherViewers, setOtherViewers] = useState<TicketViewer[]>([]);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [aiInsightCollapsed, setAiInsightCollapsed] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const assignRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const htmlTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [textColor, setTextColor] = useState('#000000');
  const [htmlMode, setHtmlMode] = useState(false);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);
  const linkPopoverRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);

  // Attachments & Emoji
  const [pendingAttachments, setPendingAttachments] = useState<TicketAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [showTranslatePicker, setShowTranslatePicker] = useState(false);
  const [translateFrom, setTranslateFrom] = useState(() => localStorage.getItem('snatched_translate_from') || 'auto');
  const [translateTo, setTranslateTo] = useState(() => localStorage.getItem('snatched_translate_to') || 'en');
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const translatePickerRef = useRef<HTMLDivElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const [sendAndClose, setSendAndClose] = useState(false);

  // Load translate language preferences from backend (syncs across devices)
  useEffect(() => {
    getUserPreferences().then(prefs => {
      if (prefs.translate_from) {
        setTranslateFrom(prefs.translate_from);
        localStorage.setItem('snatched_translate_from', prefs.translate_from);
      }
      if (prefs.translate_to) {
        setTranslateTo(prefs.translate_to);
        localStorage.setItem('snatched_translate_to', prefs.translate_to);
      }
    }).catch(() => {});
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (statusRef.current && !statusRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
      if (assignRef.current && !assignRef.current.contains(event.target as Node)) {
        setAssignDropdownOpen(false);
      }
      if (linkPopoverRef.current && !linkPopoverRef.current.contains(event.target as Node)) {
        setLinkPopoverOpen(false);
      }
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
      if (translatePickerRef.current && !translatePickerRef.current.contains(event.target as Node)) {
        setShowTranslatePicker(false);
        setShowFromDropdown(false);
        setShowToDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticketDetail?.messages]);

  // Reset reply when ticket changes
  useEffect(() => {
    setReplyContent('');
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    setAiInsightCollapsed(false);
  }, [ticketDetail?.ticket?.ticket_id]);

  useEffect(() => {
    if (!editorRef.current) return;
    const normalized = replyContent || '';
    if (editorRef.current.innerHTML !== normalized) {
      editorRef.current.innerHTML = normalized;
    }
  }, [replyContent]);

  // Collision detection: register viewer + poll for others
  useEffect(() => {
    const ticketId = ticketDetail?.ticket?.ticket_id;
    if (!ticketId) return;

    const currentUser = 'agent@snatched.store';

    registerTicketViewer(ticketId, currentUser, 'Agent').catch(() => {});

    const interval = setInterval(() => {
      registerTicketViewer(ticketId, currentUser, 'Agent').catch(() => {});
      getTicketViewers(ticketId)
        .then(res => {
          const others = (res.viewers || []).filter(v => v.user_email !== currentUser);
          setOtherViewers(others);
        })
        .catch(() => {});
    }, 30_000);

    getTicketViewers(ticketId)
      .then(res => {
        const others = (res.viewers || []).filter(v => v.user_email !== currentUser);
        setOtherViewers(others);
      })
      .catch(() => {});

    return () => {
      clearInterval(interval);
      unregisterTicketViewer(ticketId, currentUser).catch(() => {});
      setOtherViewers([]);
    };
  }, [ticketDetail?.ticket?.ticket_id]);

  // Generate AI draft
  async function handleGenerateAIDraft() {
    if (!ticketDetail || generatingDraft) return;
    setGeneratingDraft(true);
    try {
      const lastCustomerMsg = ticketDetail.messages
        ?.filter(m => m.role === 'customer')
        .pop();

      const messageContent = lastCustomerMsg?.content || ticketDetail.ticket.subject || '';

      const response = await sendChatMessage({
        message: messageContent,
        session_id: ticketDetail.ticket.session_id || ticketDetail.ticket.ticket_id,
        customer_email: ticketDetail.ticket.customer_email,
      });

      if (response?.message) {
        setReplyContent(textToHtml(response.message));
        setReplyType('email');
      }
    } catch (err) {
      console.error('Failed to generate AI draft:', err);
    } finally {
      setGeneratingDraft(false);
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <Loader2 size={24} className="animate-spin text-gray-300" />
      </div>
    );
  }

  if (!ticketDetail) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50/50 text-gray-500">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Sparkles size={24} className="text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-600">Select a ticket to view details</p>
          <p className="text-xs text-gray-400 mt-1">Use <kbd className="px-1 py-0.5 bg-gray-200 rounded text-[10px] font-mono">j</kbd>/<kbd className="px-1 py-0.5 bg-gray-200 rounded text-[10px] font-mono">k</kbd> to navigate, <kbd className="px-1 py-0.5 bg-gray-200 rounded text-[10px] font-mono">r</kbd> to reply</p>
        </div>
      </div>
    );
  }

  const { ticket, messages } = ticketDetail;
  const isSnoozed = ticket.snoozed_until ? new Date(ticket.snoozed_until) > new Date() : false;
  const hasAIData = ticket.ai_summary || ticket.category || ticket.sentiment;
  const categoryLabel = ticket.category ? (CATEGORY_LABELS[ticket.category] || ticket.category.replace(/([A-Z])/g, ' $1').trim()) : null;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  function handleSendClick() {
    if (!htmlToPlainText(replyContent) || sending) return;
    // For emails, show confirmation dialog; for notes, send immediately
    if (replyType === 'email') {
      setSendAndClose(false);
      setShowSendConfirm(true);
    } else {
      doSendMessage(false);
    }
  }

  function handleSendAndCloseClick() {
    if (!htmlToPlainText(replyContent) || sending) return;
    if (replyType === 'email') {
      setSendAndClose(true);
      setShowSendConfirm(true);
    } else {
      doSendAndClose(false);
    }
  }

  async function doSendMessage(isDraft: boolean) {
    if (!htmlToPlainText(replyContent) || sending) return;
    setShowSendConfirm(false);
    if (isDraft) {
      setSavingDraft(true);
    } else {
      setSending(true);
    }
    setSendStatus(null);
    try {
      await onAddMessage?.(ticket.ticket_id, replyContent.trim(), replyType, isDraft, pendingAttachments.length > 0 ? pendingAttachments : undefined);
      setReplyContent('');
      setPendingAttachments([]);
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
      if (isDraft) {
        setSendStatus({ type: 'success', message: 'Draft saved in Gmail. You can review and send from there.' });
      } else {
        setSendStatus({
          type: 'success',
          message: replyType === 'email' ? `Email sent to ${ticket.customer_email || 'customer'}` : 'Note saved',
        });
      }
      setTimeout(() => setSendStatus(null), 5000);
    } catch {
      setSendStatus({ type: 'error', message: 'Failed to send. Please try again.' });
      setTimeout(() => setSendStatus(null), 5000);
    } finally {
      setSending(false);
      setSavingDraft(false);
    }
  }

  async function doSendAndClose(isDraft: boolean) {
    if (!htmlToPlainText(replyContent) || sending) return;
    setShowSendConfirm(false);
    setSending(true);
    setSendStatus(null);
    try {
      await onAddMessage?.(ticket.ticket_id, replyContent.trim(), replyType, isDraft, pendingAttachments.length > 0 ? pendingAttachments : undefined);
      setReplyContent('');
      setPendingAttachments([]);
      if (editorRef.current) editorRef.current.innerHTML = '';
      onStatusChange?.(ticket.ticket_id, 'closed');
      setSendStatus({
        type: 'success',
        message: replyType === 'email' ? 'Email sent & ticket closed' : 'Note saved & ticket closed',
      });
      setTimeout(() => setSendStatus(null), 5000);
    } catch {
      setSendStatus({ type: 'error', message: 'Failed to send. Please try again.' });
      setTimeout(() => setSendStatus(null), 5000);
    } finally {
      setSending(false);
      setSendAndClose(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSendClick();
    }
  }

  function applyEditorCommand(command: string, value?: string) {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    setReplyContent(editorRef.current?.innerHTML || '');
  }

  function handleInsertLink() {
    if (linkPopoverOpen) {
      setLinkPopoverOpen(false);
      return;
    }
    // Save current selection before opening popover
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
    setLinkUrl('');
    setLinkPopoverOpen(true);
    setTimeout(() => linkInputRef.current?.focus(), 0);
  }

  function handleLinkSubmit() {
    if (!linkUrl.trim()) return;
    // Restore saved selection before applying link
    const sel = window.getSelection();
    if (savedSelectionRef.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
    applyEditorCommand('createLink', linkUrl.trim());
    setLinkPopoverOpen(false);
    setLinkUrl('');
    savedSelectionRef.current = null;
  }

  function handleEditorInput() {
    setReplyContent(editorRef.current?.innerHTML || '');
  }

  function insertTextAtCursor(content: string) {
    editorRef.current?.focus();
    document.execCommand('insertText', false, content);
    setReplyContent(editorRef.current?.innerHTML || '');
  }

  async function handleFileUpload(files: FileList | null) {
    if (!files || !ticket) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const result = await uploadTicketAttachment(ticket.ticket_id, file);
        setPendingAttachments(prev => [...prev, result]);
      }
    } catch (err) {
      console.error('Attachment upload failed:', err);
      setSendStatus({ type: 'error', message: err instanceof Error ? err.message : 'Failed to upload attachment' });
      setTimeout(() => setSendStatus(null), 5000);
    } finally {
      setUploading(false);
    }
  }

  function removeAttachment(attachmentId: string) {
    setPendingAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
  }

  async function handleTranslate() {
    const text = editorRef.current?.innerText?.trim();
    if (!text) return;
    setShowTranslatePicker(false);
    setTranslating(true);
    try {
      const opts: { source_language?: string; target_language?: string } = {
        target_language: translateTo,
      };
      if (translateFrom !== 'auto') {
        opts.source_language = translateFrom;
      }
      const { translated_text } = await translateText(text, opts);
      if (editorRef.current) {
        editorRef.current.innerHTML = translated_text;
        setReplyContent(translated_text);
      }
    } catch (err: any) {
      console.error('Translation failed:', err);
      const msg = err?.message || 'Translation failed. Please try again.';
      toast.error(msg);
    } finally {
      setTranslating(false);
    }
  }

  function renderMessage(message: TicketMessage, index: number) {
    // System events
    if (message.role === 'system' || message.type === 'status_change' || message.type === 'assignment' || message.type === 'tag_change') {
      return (
        <div key={message.message_id || index} className="flex justify-center my-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] text-gray-500 bg-gray-100 rounded-full">
            {message.type === 'assignment' && <User size={11} />}
            {message.content}
            <span className="text-gray-400 ml-1">{formatDate(message.created_at)}</span>
          </span>
        </div>
      );
    }

    // Internal notes
    if (message.type === 'note') {
      return (
        <div key={message.message_id || index} className="mb-4">
          <div className="bg-amber-50/60 border border-amber-200/60 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <StickyNote size={12} className="text-amber-600" />
              <span className="text-[11px] font-medium text-amber-700">Internal Note</span>
              <span className="text-[11px] text-amber-500 ml-auto">{formatDate(message.created_at)}</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-amber-200 flex items-center justify-center text-amber-800 text-xs font-semibold flex-shrink-0">
                {message.author_name[0]?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-amber-900 mb-1">{message.author_name}</p>
                <div
                  className="text-[13px] text-amber-800 whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.content) }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Customer messages
    if (message.role === 'customer') {
      return (
        <div key={message.message_id || index} className="mb-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 text-sm font-semibold flex-shrink-0">
                {message.author_name[0]?.toUpperCase() || 'C'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-[13px] font-semibold text-gray-900">{message.author_name}</p>
                  {message.type === 'email' && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                      <Mail size={10} /> via Email
                    </span>
                  )}
                  <span className="text-[11px] text-gray-400 ml-auto tabular-nums">{formatDate(message.created_at)}</span>
                </div>
                <div className="text-[13px] text-gray-700 whitespace-pre-wrap leading-relaxed">{message.content}</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Agent replies
    return (
      <div key={message.message_id || index} className="mb-4">
        <div className="bg-blue-50/50 border border-blue-200/60 rounded-xl p-4 ml-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 text-sm font-semibold flex-shrink-0">
              {message.author_name[0]?.toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[13px] font-semibold text-blue-900">{message.author_name}</p>
                {message.type === 'email' && message.is_sent && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-emerald-100 text-emerald-700 rounded-md font-medium">
                    <Mail size={9} /> Sent
                  </span>
                )}
                {message.type === 'email' && message.draft_id && !message.is_sent && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-md font-medium">
                    <Mail size={9} /> Draft
                  </span>
                )}
                {message.is_draft && message.type !== 'email' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-md font-medium">Draft</span>
                )}
                <span className="text-[11px] text-blue-500 ml-auto tabular-nums">{formatDate(message.created_at)}</span>
              </div>
              <div
                className="text-[13px] text-blue-800 whitespace-pre-wrap leading-relaxed break-words"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.content) }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* Header — compact */}
      <div className="flex-shrink-0 border-b border-gray-200/80 bg-white px-5 py-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-base font-bold text-gray-900 truncate">
                {ticket.subject || 'No Subject'}
              </h1>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Status dropdown */}
              <div className="relative" ref={statusRef}>
                <button
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  className="flex items-center gap-1"
                >
                  <StatusBadge status={ticket.status} />
                  <ChevronDown size={12} className="text-gray-400" />
                </button>
                {statusDropdownOpen && (
                  <div className="absolute left-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-xl z-50 animate-dropdown">
                    <div className="p-1">
                      {STATUS_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            onStatusChange?.(ticket.ticket_id, option.value);
                            setStatusDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-1.5 text-[13px] rounded-lg transition-colors ${
                            ticket.status === option.value
                              ? 'bg-amber-50 text-amber-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <PriorityBadge priority={ticket.priority} size="sm" />

              <span className="text-[11px] text-gray-400 font-mono">{ticket.ticket_id?.slice(0, 8)}</span>

              {/* Assignment dropdown */}
              <div className="relative ml-auto" ref={assignRef}>
                <button
                  onClick={() => setAssignDropdownOpen(!assignDropdownOpen)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <Bot size={13} className={ticket.assigned_agent_name ? 'text-amber-500' : 'text-gray-400'} />
                  <span className={ticket.assigned_agent_name ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                    {ticket.assigned_agent_name || 'Unassigned'}
                  </span>
                  <ChevronDown size={11} className="text-gray-400" />
                </button>
                {assignDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-xl z-50 animate-dropdown">
                    <div className="p-1">
                      <button
                        onClick={() => {
                          onAssign?.(ticket.ticket_id, '', '');
                          setAssignDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-[13px] rounded-lg transition-colors ${
                          !ticket.assigned_to
                            ? 'bg-amber-50 text-amber-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Unassigned
                      </button>
                      {agents.map((agent) => (
                        <button
                          key={agent.id}
                          onClick={() => {
                            onAssign?.(ticket.ticket_id, agent.id, agent.name);
                            setAssignDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-1.5 text-[13px] rounded-lg transition-colors ${
                            ticket.assigned_to === agent.id
                              ? 'bg-amber-50 text-amber-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {agent.name}
                        </button>
                      ))}
                      {agents.length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-400">No agents configured</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <SnoozePicker
                isSnoozed={isSnoozed}
                onSnooze={(snoozeUntil) => onSnooze?.(ticket.ticket_id, snoozeUntil)}
              />

              {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                <button
                  onClick={() => onStatusChange?.(ticket.ticket_id, 'resolved')}
                  className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/10 rounded-lg text-xs font-medium hover:bg-emerald-100 transition-colors"
                >
                  Resolve
                </button>
              )}

              {ticket.status === 'resolved' && (
                <button
                  onClick={() => onStatusChange?.(ticket.ticket_id, 'closed')}
                  className="flex items-center gap-1 px-2.5 py-1 bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-500/10 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              )}

              {(ticket.status === 'closed' || ticket.status === 'resolved') && (
                <button
                  onClick={() => onStatusChange?.(ticket.ticket_id, 'open')}
                  className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/10 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors"
                >
                  Reopen
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Collision detection */}
        {otherViewers.length > 0 && (
          <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 bg-orange-50 border border-orange-200/60 rounded-lg">
            <div className="flex -space-x-1">
              {otherViewers.map(v => (
                <div key={v.user_email} className="w-5 h-5 rounded-full bg-orange-400 text-white text-[10px] flex items-center justify-center border border-white font-medium">
                  {v.user_name?.[0]?.toUpperCase() || '?'}
                </div>
              ))}
            </div>
            <span className="text-[11px] text-orange-700">
              {otherViewers.map(v => v.user_name || v.user_email).join(', ')} {otherViewers.length === 1 ? 'is' : 'are'} also viewing
            </span>
          </div>
        )}

        {/* SLA Timer */}
        {ticket.metrics && (
          <div className="mt-2">
            <SlaTimer ticket={ticket} />
          </div>
        )}

        {/* Snooze indicator */}
        {isSnoozed && ticket.snoozed_until && (
          <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 bg-amber-50 border border-amber-200/60 rounded-lg">
            <AlertTriangle size={12} className="text-amber-600" />
            <span className="text-[11px] text-amber-700">
              Snoozed until {formatDate(ticket.snoozed_until)}
            </span>
          </div>
        )}
      </div>

      {/* AI Insight Card — THE DIFFERENTIATOR */}
      {hasAIData && !aiInsightCollapsed && (
        <div className="flex-shrink-0 border-b border-purple-100 bg-gradient-to-r from-purple-50/80 via-indigo-50/50 to-blue-50/30 px-5 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1 rounded-md bg-purple-100">
                <Brain size={13} className="text-purple-600" />
              </div>
              <span className="text-[11px] font-semibold text-purple-700 uppercase tracking-wide">AI Analysis</span>
            </div>
            <button
              onClick={() => setAiInsightCollapsed(true)}
              className="text-[10px] text-purple-400 hover:text-purple-600 transition-colors"
            >
              Dismiss
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {/* Classification */}
            {categoryLabel && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Category</p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-purple-100 text-purple-800">
                  <Zap size={10} />
                  {categoryLabel}
                </span>
              </div>
            )}

            {/* Sentiment */}
            {ticket.sentiment && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Sentiment</p>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md ${
                  ticket.sentiment === 'positive' ? 'bg-emerald-100 text-emerald-800' :
                  ticket.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {ticket.sentiment === 'positive' && <TrendingUp size={10} />}
                  {ticket.sentiment === 'negative' && <TrendingDown size={10} />}
                  {ticket.sentiment === 'neutral' && <Minus size={10} />}
                  {ticket.sentiment.charAt(0).toUpperCase() + ticket.sentiment.slice(1)}
                </span>
              </div>
            )}

            {/* Assigned AI Agent */}
            {ticket.assigned_agent_name && (
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">AI Agent</p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-amber-100 text-amber-800">
                  <Bot size={10} />
                  {ticket.assigned_agent_name}
                </span>
              </div>
            )}
          </div>

          {/* AI Summary */}
          {ticket.ai_summary && (
            <div className="mt-2.5 p-2.5 bg-white/60 rounded-lg border border-purple-100/60">
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">AI Summary</p>
              <p className="text-[12px] text-gray-700 leading-relaxed">{ticket.ai_summary}</p>
            </div>
          )}

          {/* Human review flag */}
          {ticket.requires_human_review && (
            <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 border border-red-200/60 rounded-lg">
              <ShieldAlert size={12} className="text-red-600" />
              <span className="text-[11px] font-medium text-red-700">Flagged for human review</span>
            </div>
          )}
        </div>
      )}

      {/* Collapsed AI badge — click to expand */}
      {hasAIData && aiInsightCollapsed && (
        <div className="flex-shrink-0 border-b border-gray-100">
          <button
            onClick={() => setAiInsightCollapsed(false)}
            className="w-full px-5 py-1.5 flex items-center gap-2 text-[11px] text-purple-500 hover:text-purple-700 hover:bg-purple-50/50 transition-colors"
          >
            <Brain size={12} />
            <span>Show AI analysis</span>
            {categoryLabel && <span className="text-purple-400">— {categoryLabel}</span>}
            {ticket.sentiment === 'negative' && (
              <span className="ml-auto text-red-500 font-medium flex items-center gap-1">
                <TrendingDown size={10} /> Negative
              </span>
            )}
          </button>
        </div>
      )}

      {/* Messages Timeline */}
      <div className="flex-1 overflow-y-auto p-5">
        <div className="max-w-3xl mx-auto">
          {messages && messages.length > 0 ? (
            messages.map((msg, idx) => renderMessage(msg, idx))
          ) : (
            <div className="text-center text-[13px] text-gray-400 py-8">
              <p>No messages yet</p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Reply Box */}
      <div className="flex-shrink-0 border-t border-gray-200/80 bg-white px-5 py-3">
        <div className="max-w-3xl mx-auto">
          {/* Send status feedback */}
          {sendStatus && (
            <div className={`mb-2 px-3 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-2 ${
              sendStatus.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/10'
                : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10'
            }`}>
              {sendStatus.type === 'success' ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              ) : (
                <AlertTriangle size={12} />
              )}
              {sendStatus.message}
            </div>
          )}

          {/* Reply type toggle + AI Draft button */}
          <div className="flex items-center gap-1 mb-2">
            <button
              onClick={() => setReplyType('email')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                replyType === 'email'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Mail size={11} />
              Reply
            </button>
            <button
              onClick={() => setReplyType('note')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                replyType === 'note'
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <StickyNote size={11} />
              Note
            </button>

            {/* AI Draft button — THE KEY DIFFERENTIATOR */}
            <button
              onClick={handleGenerateAIDraft}
              disabled={generatingDraft}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors disabled:opacity-50 ml-auto ring-1 ring-inset ring-purple-200"
            >
              {generatingDraft ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <Wand2 size={11} />
              )}
              {generatingDraft ? 'Generating...' : 'AI Draft'}
            </button>

          </div>

          {replyType === 'email' && ticket.customer_email && (
            <div className="flex items-center gap-2 px-3 py-1.5 mb-1 text-[12px] text-gray-500 border border-gray-200 rounded-lg bg-gray-50/50">
              <span className="font-medium text-gray-400">To:</span>
              <span className="text-gray-700">
                {ticket.customer_name && <span className="font-medium">{ticket.customer_name} </span>}
                ({ticket.customer_email})
              </span>
            </div>
          )}

          <div className={`w-full border rounded-xl overflow-hidden transition-all ${
            replyType === 'note'
              ? 'border-amber-200 bg-amber-50/50 focus-within:ring-2 focus-within:ring-amber-500/30'
              : 'border-gray-200 focus-within:ring-2 focus-within:ring-blue-500/30'
          }`}>
            <div className="flex items-center gap-0.5 border-b border-gray-200/80 px-1.5 py-1 bg-white/80 flex-wrap">
              {/* Undo / Redo */}
              <button type="button" onClick={() => applyEditorCommand('undo')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Undo (Ctrl+Z)">
                <Undo2 size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('redo')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Redo (Ctrl+Y)">
                <Redo2 size={14} />
              </button>

              <div className="w-px h-4 bg-gray-200 mx-1 shrink-0" />

              {/* Font Family */}
              <select
                onChange={(e) => applyEditorCommand('fontName', e.target.value)}
                className="text-[11px] text-gray-600 bg-transparent border border-gray-200 rounded px-1.5 py-1 outline-none hover:bg-gray-50 cursor-pointer h-7"
                defaultValue="Arial, Helvetica, sans-serif"
              >
                <option value="Arial, Helvetica, sans-serif">Sans Serif</option>
                <option value="Georgia, Times, serif">Serif</option>
                <option value="'Courier New', monospace">Monospace</option>
                <option value="Verdana, Geneva, sans-serif">Verdana</option>
                <option value="Tahoma, Geneva, sans-serif">Tahoma</option>
              </select>

              {/* Font Size */}
              <select
                onChange={(e) => applyEditorCommand('fontSize', e.target.value)}
                className="text-[11px] text-gray-600 bg-transparent border border-gray-200 rounded px-1.5 py-1 outline-none hover:bg-gray-50 cursor-pointer h-7"
                defaultValue="3"
              >
                <option value="1">Small</option>
                <option value="2">Medium</option>
                <option value="3">Normal</option>
                <option value="4">Large</option>
                <option value="5">X-Large</option>
              </select>

              <div className="w-px h-4 bg-gray-200 mx-1 shrink-0" />

              {/* Text Style */}
              <button type="button" onClick={() => applyEditorCommand('bold')} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Bold (Ctrl+B)">
                <Bold size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('italic')} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Italic (Ctrl+I)">
                <Italic size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('underline')} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Underline (Ctrl+U)">
                <Underline size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('strikeThrough')} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Strikethrough">
                <Strikethrough size={14} />
              </button>
              <button
                type="button"
                onClick={() => colorInputRef.current?.click()}
                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors flex flex-col items-center"
                title="Text color"
              >
                <Type size={14} />
                <div className="w-3.5 h-1 rounded-sm -mt-0.5" style={{ backgroundColor: textColor }} />
              </button>
              <input
                ref={colorInputRef}
                type="color"
                value={textColor}
                onChange={(e) => {
                  setTextColor(e.target.value);
                  applyEditorCommand('foreColor', e.target.value);
                }}
                className="sr-only"
              />

              <div className="w-px h-4 bg-gray-200 mx-1 shrink-0" />

              {/* Alignment */}
              <button type="button" onClick={() => applyEditorCommand('justifyLeft')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Align left">
                <AlignLeft size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('justifyCenter')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Align center">
                <AlignCenter size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('justifyRight')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Align right">
                <AlignRight size={14} />
              </button>

              <div className="w-px h-4 bg-gray-200 mx-1 shrink-0" />

              {/* Lists & Indent */}
              <button type="button" onClick={() => applyEditorCommand('insertOrderedList')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Numbered list">
                <ListOrdered size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('insertUnorderedList')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Bullet list">
                <List size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('indent')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Increase indent">
                <Indent size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('outdent')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Decrease indent">
                <Outdent size={14} />
              </button>

              <div className="w-px h-4 bg-gray-200 mx-1 shrink-0" />

              {/* Quote & Link */}
              <button type="button" onClick={() => applyEditorCommand('formatBlock', 'blockquote')} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Quote">
                <Quote size={14} />
              </button>
              <div className="relative" ref={linkPopoverRef}>
                <button
                  type="button"
                  onClick={handleInsertLink}
                  className={`p-1.5 rounded transition-colors ${linkPopoverOpen ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                  title="Insert link"
                >
                  <Link2 size={14} />
                </button>
                {linkPopoverOpen && (
                  <div className="absolute left-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-2 flex items-center gap-1.5 w-72">
                    <input
                      ref={linkInputRef}
                      type="url"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleLinkSubmit(); }
                        if (e.key === 'Escape') setLinkPopoverOpen(false);
                      }}
                      placeholder="https://example.com"
                      className="flex-1 text-[12px] px-2.5 py-1.5 border border-gray-200 rounded-md outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30"
                    />
                    <button
                      type="button"
                      onClick={handleLinkSubmit}
                      disabled={!linkUrl.trim()}
                      className="px-2.5 py-1.5 text-[11px] font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>

              <div className="w-px h-4 bg-gray-200 mx-1 shrink-0" />

              {/* HTML Source & Remove Formatting */}
              <button
                type="button"
                onClick={() => {
                  if (htmlMode) {
                    // Switching from HTML source back to rich text
                    const html = htmlTextareaRef.current?.value || '';
                    setReplyContent(html);
                    if (editorRef.current) editorRef.current.innerHTML = html;
                  }
                  setHtmlMode(!htmlMode);
                }}
                className={`p-1.5 rounded transition-colors ${htmlMode ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                title="Edit HTML source"
              >
                <Code size={14} />
              </button>
              <button type="button" onClick={() => applyEditorCommand('removeFormat')} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded transition-colors ml-auto" title="Remove formatting">
                <RemoveFormatting size={14} />
              </button>
            </div>

            {htmlMode ? (
              <textarea
                ref={htmlTextareaRef}
                defaultValue={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full min-h-[112px] px-4 py-3 text-[13px] text-gray-800 font-mono bg-gray-50 outline-none resize-y"
                placeholder="<p>Write HTML here...</p>"
                spellCheck={false}
              />
            ) : (
              <div
                ref={editorRef}
                contentEditable
                onInput={handleEditorInput}
                onKeyDown={handleKeyDown}
                data-placeholder={
                  replyType === 'note'
                    ? 'Write an internal note...'
                    : 'Type your reply or click AI Draft to generate one...'
                }
                className="min-h-[112px] px-4 py-3 text-[13px] text-gray-800 outline-none [&:empty:before]:content-[attr(data-placeholder)] [&:empty:before]:text-gray-400 [&_a]:text-blue-600 [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:ml-1 [&_blockquote]:text-gray-500"
                suppressContentEditableWarning
              />
            )}

            {/* Attachment preview chips */}
            {pendingAttachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t border-gray-100">
                {pendingAttachments.map(att => (
                  <span key={att.attachment_id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-[11px] text-gray-600 max-w-[200px]">
                    <Paperclip size={10} className="shrink-0" />
                    <span className="truncate">{att.filename}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(att.attachment_id)}
                      className="shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
                {uploading && <Loader2 size={12} className="animate-spin text-gray-400" />}
              </div>
            )}
          </div>

          {/* Hidden file inputs */}
          <input ref={fileInputRef} type="file" className="hidden"
            accept=".pdf,.doc,.docx,.txt,.csv,.json,.xlsx"
            onChange={e => { handleFileUpload(e.target.files); e.target.value = ''; }} multiple />
          <input ref={imageInputRef} type="file" className="hidden"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={e => { handleFileUpload(e.target.files); e.target.value = ''; }} multiple />
          <input ref={videoInputRef} type="file" className="hidden"
            accept="video/mp4,video/quicktime,video/webm"
            onChange={e => { handleFileUpload(e.target.files); e.target.value = ''; }} multiple />

          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400">
                {replyType === 'email' ? 'Cmd+Enter to send' : 'Cmd+Enter to save'}
              </span>
              <MacroSelector
                onSelect={(content) => insertTextAtCursor((replyContent ? '\n' : '') + content)}
                customerName={ticket.customer_name}
                customerEmail={ticket.customer_email}
              />

              <div className="w-px h-4 bg-gray-200" />

              {/* Translate button with language picker */}
              <div className="relative" ref={translatePickerRef}>
                <button type="button"
                  onClick={() => setShowTranslatePicker(!showTranslatePicker)}
                  disabled={translating || !replyContent}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                  title="Translate reply">
                  {translating ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />}
                </button>

                {showTranslatePicker && (() => {
                  const langOptions = [
                    { value: 'en', label: 'English', flag: '🇬🇧' },
                    { value: 'es', label: 'Spanish', flag: '🇪🇸' },
                    { value: 'no', label: 'Norwegian', flag: '🇳🇴' },
                    { value: 'de', label: 'German', flag: '🇩🇪' },
                    { value: 'fr', label: 'French', flag: '🇫🇷' },
                    { value: 'pt', label: 'Portuguese', flag: '🇵🇹' },
                    { value: 'it', label: 'Italian', flag: '🇮🇹' },
                    { value: 'sv', label: 'Swedish', flag: '🇸🇪' },
                    { value: 'da', label: 'Danish', flag: '🇩🇰' },
                    { value: 'nl', label: 'Dutch', flag: '🇳🇱' },
                    { value: 'fi', label: 'Finnish', flag: '🇫🇮' },
                    { value: 'pl', label: 'Polish', flag: '🇵🇱' },
                    { value: 'ja', label: 'Japanese', flag: '🇯🇵' },
                    { value: 'ko', label: 'Korean', flag: '🇰🇷' },
                    { value: 'zh', label: 'Chinese', flag: '🇨🇳' },
                    { value: 'ar', label: 'Arabic', flag: '🇸🇦' },
                  ];
                  const fromOptions = [{ value: 'auto', label: 'Auto-detect', flag: '🌐' }, ...langOptions];
                  const getLabel = (value: string, options: typeof fromOptions) => options.find(o => o.value === value);

                  return (
                    <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-50 w-72">
                      <p className="text-xs font-medium text-gray-700 mb-2">Translate</p>
                      <div className="flex items-center gap-2">
                        {/* From selector */}
                        <div className="relative flex-1">
                          <button
                            type="button"
                            onClick={() => { setShowFromDropdown(!showFromDropdown); setShowToDropdown(false); }}
                            className="w-full flex items-center justify-between px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-md hover:border-teal-300 transition-all focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                          >
                            <span className="flex items-center gap-1.5 truncate">
                              <span className="text-sm leading-none">{getLabel(translateFrom, fromOptions)?.flag}</span>
                              <span className="text-gray-700">{getLabel(translateFrom, fromOptions)?.label}</span>
                            </span>
                            <ChevronDown size={12} className={`text-gray-400 flex-shrink-0 transition-transform ${showFromDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          {showFromDropdown && (
                            <div className="absolute bottom-full left-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-100 max-h-52 overflow-y-auto z-[60] min-w-[10rem]">
                              {fromOptions.map(opt => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    setTranslateFrom(opt.value);
                                    localStorage.setItem('snatched_translate_from', opt.value);
                                    updateUserPreferences({ translate_from: opt.value }).catch(() => {});
                                    setShowFromDropdown(false);
                                  }}
                                  className={`w-full flex items-center justify-between px-2.5 py-1.5 text-xs hover:bg-teal-50 transition-colors ${translateFrom === opt.value ? 'bg-teal-50 text-teal-700' : 'text-gray-600'}`}
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="text-sm leading-none">{opt.flag}</span>
                                    <span className="font-medium">{opt.label}</span>
                                  </span>
                                  {translateFrom === opt.value && <Check size={11} className="text-teal-500 flex-shrink-0" />}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <ArrowRight size={14} className="text-gray-400 flex-shrink-0" />

                        {/* To selector */}
                        <div className="relative flex-1">
                          <button
                            type="button"
                            onClick={() => { setShowToDropdown(!showToDropdown); setShowFromDropdown(false); }}
                            className="w-full flex items-center justify-between px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-md hover:border-teal-300 transition-all focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                          >
                            <span className="flex items-center gap-1.5 truncate">
                              <span className="text-sm leading-none">{getLabel(translateTo, langOptions)?.flag}</span>
                              <span className="text-gray-700">{getLabel(translateTo, langOptions)?.label}</span>
                            </span>
                            <ChevronDown size={12} className={`text-gray-400 flex-shrink-0 transition-transform ${showToDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          {showToDropdown && (
                            <div className="absolute bottom-full right-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-100 max-h-52 overflow-y-auto z-[60] min-w-[10rem]">
                              {langOptions.map(opt => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    setTranslateTo(opt.value);
                                    localStorage.setItem('snatched_translate_to', opt.value);
                                    updateUserPreferences({ translate_to: opt.value }).catch(() => {});
                                    setShowToDropdown(false);
                                  }}
                                  className={`w-full flex items-center justify-between px-2.5 py-1.5 text-xs hover:bg-teal-50 transition-colors ${translateTo === opt.value ? 'bg-teal-50 text-teal-700' : 'text-gray-600'}`}
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="text-sm leading-none">{opt.flag}</span>
                                    <span className="font-medium">{opt.label}</span>
                                  </span>
                                  {translateTo === opt.value && <Check size={11} className="text-teal-500 flex-shrink-0" />}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setShowFromDropdown(false); setShowToDropdown(false); handleTranslate(); }}
                        disabled={translating}
                        className="mt-2 w-full text-xs font-medium px-3 py-1.5 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                      >
                        {translating ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
                        Translate
                      </button>
                    </div>
                  );
                })()}
              </div>

              <div className="w-px h-4 bg-gray-200" />

              {/* Attachment buttons */}
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50" title="Attach file">
                <Paperclip size={14} />
              </button>
              <button type="button" onClick={() => imageInputRef.current?.click()} disabled={uploading}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50" title="Attach image">
                <Image size={14} />
              </button>
              <button type="button" onClick={() => videoInputRef.current?.click()} disabled={uploading}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50" title="Attach video">
                <Film size={14} />
              </button>

              {/* Emoji picker */}
              <div className="relative" ref={emojiPickerRef}>
                <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className={`p-1.5 rounded transition-colors ${showEmojiPicker ? 'bg-amber-100 text-amber-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                  title="Insert emoji">
                  <Smile size={14} />
                </button>
                {showEmojiPicker && (
                  <div className="absolute bottom-full left-0 mb-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-2 w-[280px]">
                    <div className="grid grid-cols-8 gap-0.5 max-h-[200px] overflow-y-auto">
                      {['😀','😃','😄','😁','😂','🤣','😊','😇','🙂','😉','😌','😍','🥰','😘','😗','😋','😛','😜','🤪','🤔','🤨','😐','😑','😶','🙄','😏','😣','😥','😮','🤐','😯','😪','😫','🥱','😴','😤','😠','😡','🤬','😈','👿','💀','☠️','💩','🤡','👹','👻','👽','🤖','😺','😸','🙏','👍','👎','👊','✊','🤛','🤜','🤝','👏','🙌','👐','🤲','🤞','✌️','🤟','🤘','👌','🤌','👈','👉','👆','👇','☝️','✋','🤚','🖐','❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','❣️','💕','💯','💢','💥','🔥','✨','⭐','🌟','💫','🎉','🎊','🎈','📎','📷','🎥','📁','📂','✅','❌','⚠️','❓','❗','💡','📌','🔗','📧','📞'].map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => {
                            insertTextAtCursor(emoji);
                            setShowEmojiPicker(false);
                          }}
                          className="p-1 text-lg hover:bg-gray-100 rounded transition-colors leading-none"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {replyType === 'email' && (
                <button
                  onClick={() => doSendMessage(true)}
                  disabled={!htmlToPlainText(replyContent) || sending || savingDraft}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-xl font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingDraft ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Mail size={13} />
                  )}
                  Save Draft
                </button>
              )}
              <button
                onClick={handleSendAndCloseClick}
                disabled={!htmlToPlainText(replyContent) || sending || savingDraft}
                className="flex items-center gap-1.5 px-4 py-2 text-xs rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                <Send size={13} />
                {replyType === 'note' ? 'Save & Close' : 'Send & Close'}
              </button>
              <button
                onClick={handleSendClick}
                disabled={!htmlToPlainText(replyContent) || sending || savingDraft}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  replyType === 'note'
                    ? 'text-amber-700 bg-amber-100 hover:bg-amber-200'
                    : 'text-white bg-gray-900 hover:bg-gray-800'
                }`}
              >
                {sending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Send size={13} />
                )}
                {replyType === 'note' ? 'Save Note' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Send Confirmation Dialog */}
      {showSendConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 animate-fade-in">
            <h3 className="text-[15px] font-semibold text-gray-900 mb-1">Send email?</h3>
            <p className="text-[13px] text-gray-500 mb-4">
              This will send an email to <span className="font-medium text-gray-700">{ticket.customer_email}</span>
            </p>
            <div className="bg-gray-50 rounded-xl p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-[12px] text-gray-600 whitespace-pre-wrap">{htmlToPlainText(replyContent).slice(0, 500)}{htmlToPlainText(replyContent).length > 500 ? '...' : ''}</p>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowSendConfirm(false); setSendAndClose(false); }}
                className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => doSendMessage(true)}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Save as Draft
              </button>
              <button
                onClick={() => sendAndClose ? doSendAndClose(false) : doSendMessage(false)}
                className="px-4 py-2 text-xs font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
              >
                {sendAndClose ? 'Send & Close' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
